import React, { useEffect, useMemo, useState } from 'react';

import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Search as SearchIcon, SearchX } from 'lucide-react';

import Axios from '../../Assets/Axios/Axios';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import Reveal from '../../Components/Reveal';
import { Stagger, StaggerItem } from '../../Components/Motion';

import { COLORS } from '../../Constant/COLORS';

import { getCategoryIcon } from '../../Helpers/categoryIcon';
import { toCapitalize } from '../../Helpers';
import getThumbnailImage from '../../Helpers/getThumbnailImage';

import Card, { SkeletonCard } from '../Category/Components/Card';

import { ServicesQuery, GetCategories } from '../../Server/Query';

// Shared cache so revisiting search doesn't re-fetch the whole catalogue.
let catalogueRequest = null;
const loadCatalogue = () => {
  if (!catalogueRequest) {
    catalogueRequest = Promise.all([Axios(ServicesQuery), Axios(GetCategories)]).catch(
      error => {
        catalogueRequest = null;
        throw error;
      }
    );
  }
  return catalogueRequest;
};

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';

  const [term, setTerm] = useState(query);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Keep the input in sync when the URL query changes (e.g. from the header).
  useEffect(() => setTerm(query), [query]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    loadCatalogue()
      .then(([servicesRes, categoriesRes]) => {
        if (!active) return undefined;
        setServices(
          (servicesRes?.data?.Services ?? []).map(service => ({
            ...service,
            banner_image: service.banner_image ?? getThumbnailImage(service.name),
          }))
        );
        setCategories(categoriesRes?.data?.categories ?? []);
        return undefined;
      })
      .catch(() => undefined)
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const q = query.trim().toLowerCase();

  const matchedServices = useMemo(() => {
    if (!q) return [];
    return services.filter(service =>
      [
        service.name,
        service.description,
        service.professional_name,
        service.category_name,
      ]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(q))
    );
  }, [services, q]);

  const matchedCategories = useMemo(() => {
    if (!q) return [];
    return categories.filter(category =>
      [category.name, category.description]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(q))
    );
  }, [categories, q]);

  const submit = event => {
    event.preventDefault();
    const next = term.trim();
    setSearchParams(next ? { q: next } : {});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalResults = matchedServices.length + matchedCategories.length;

  return (
    <>
      <Header isSellerOrCategoryPage />
      <Box maxWidth={{ xs: '90%', md: '80%' }} mx='auto' mt={{ xs: 4, md: 6 }} mb={10}>
        <Reveal>
          <Typography variant='overline' color={COLORS.teal} display='block'>
            Search
          </Typography>
          <Typography variant='h3' mb={2}>
            {query ? (
              <>
                Results for{' '}
                <Box component='span' color={COLORS.teal}>
                  &ldquo;{query}&rdquo;
                </Box>
              </>
            ) : (
              'Search Evento'
            )}
          </Typography>

          <Box component='form' onSubmit={submit}>
            <TextField
              value={term}
              onChange={event => setTerm(event.target.value)}
              placeholder='Search a service, professional or category'
              sx={{ width: '100%', maxWidth: 520, mb: 1 }}
              InputProps={{
                sx: { borderRadius: 999 },
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {query && !isLoading && (
            <Typography color={COLORS.inkSoft} mb={4}>
              {totalResults === 0
                ? 'No matches found.'
                : `${totalResults} result${totalResults === 1 ? '' : 's'} found.`}
            </Typography>
          )}
        </Reveal>

        {/* Matching categories */}
        {matchedCategories.length > 0 && (
          <Box mb={5}>
            <Typography variant='overline' color={COLORS.inkSoft} display='block' mb={1.5}>
              Categories
            </Typography>
            <Stack direction='row' flexWrap='wrap' gap={1.25}>
              {matchedCategories.map(category => {
                const Icon = getCategoryIcon(category.name || category.slug);
                return (
                  <Chip
                    key={category.id}
                    component={RouterLink}
                    to={`/category/${category.slug}`}
                    clickable
                    icon={<Icon size={16} strokeWidth={1.8} />}
                    label={toCapitalize(category.name)}
                    sx={{
                      px: 0.5,
                      bgcolor: COLORS.surface,
                      border: `1px solid ${COLORS.hairline}`,
                      '& .MuiChip-icon': { color: COLORS.teal },
                      '&:hover': { borderColor: COLORS.teal, bgcolor: COLORS.tealTint },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Matching services */}
        {matchedServices.length > 0 && (
          <Typography variant='overline' color={COLORS.inkSoft} display='block' mb={1.5}>
            Professionals
          </Typography>
        )}

        <Box
          display='grid'
          gap='2em'
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }}
        >
          {isLoading &&
            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}

          {!isLoading &&
            matchedServices.map((service, index) => (
              <Reveal key={service.id} delay={Math.min(index, 6) * 0.05} style={{ height: '100%' }}>
                <Card
                  service={service}
                  href={`/professional/${service.service_id ?? service.serviceId ?? service.id}`}
                />
              </Reveal>
            ))}
        </Box>

        {/* Empty state */}
        {!isLoading && query && totalResults === 0 && (
          <Stagger>
            <StaggerItem>
              <Stack
                alignItems='center'
                textAlign='center'
                gap={1.5}
                py={8}
                px={3}
                sx={{ border: `1px dashed ${COLORS.hairline}`, borderRadius: 4, bgcolor: COLORS.surface }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: COLORS.surfaceAlt,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SearchX size={30} color={COLORS.inkSoft} />
                </Box>
                <Typography variant='h5'>No results for &ldquo;{query}&rdquo;</Typography>
                <Typography color={COLORS.inkSoft} maxWidth={400}>
                  Try a different keyword, or browse all categories to find the right
                  professional for your event.
                </Typography>
                <Chip
                  component={RouterLink}
                  to='/categories'
                  clickable
                  label='Browse all categories'
                  sx={{ mt: 1, bgcolor: COLORS.teal, color: '#fff', fontWeight: 600 }}
                />
              </Stack>
            </StaggerItem>
          </Stagger>
        )}

        {!isLoading && !query && (
          <Typography color={COLORS.inkSoft}>
            Type something above to search across services, professionals and categories.
          </Typography>
        )}
      </Box>
      <Footer />
    </>
  );
};

export default SearchResults;
